import React, { useState, useEffect } from 'react';
import api from '../services/api';
import { ArrowPathIcon, NoSymbolIcon, FunnelIcon, CloudIcon } from '@heroicons/react/24/outline';

export default function CloudflarePage() {
  const [logs, setLogs] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [blockIP, setBlockIP] = useState('');
  const [blockReason, setBlockReason] = useState('');
  const [blocking, setBlocking] = useState(false);
  const [blockMsg, setBlockMsg] = useState(null);
  const [cfError, setCfError] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const [syncMsg, setSyncMsg] = useState(null);
  const [hostFilter, setHostFilter] = useState('');
  const [search, setSearch] = useState('');
  const [availableHosts, setAvailableHosts] = useState([]);
  const limit = 20;

  useEffect(() => { fetchLogs(); }, [page, hostFilter, search]);

  async function fetchLogs() {
    setLoading(true);
    try {
      const res = await api.get('/cloudflare/logs', { 
        params: { page, limit, hostFilter, search } 
      });
      setLogs(res.data.logs || []);
      setTotal(res.data.total || 0);
      if (res.data.availableHosts) setAvailableHosts(res.data.availableHosts);
      if (res.data.cfError) {
        setCfError(res.data.cfError);
      } else if (res.data.fetchedNow > 0) {
        setCfError(`✓ Berhasil fetch ${res.data.fetchedNow} events dari ${res.data.zonesCount} zones`);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }

  async function handleBlock(e) {
    e.preventDefault();
    if (!blockIP) return;
    setBlocking(true);
    setBlockMsg(null);
    try {
      const res = await api.post('/cloudflare/block-ip', { ip_address: blockIP, reason: blockReason });
      setBlockMsg({ type: 'success', text: res.data.message });
      setBlockIP('');
      setBlockReason('');
    } catch (err) {
      setBlockMsg({ type: 'error', text: err.response?.data?.message || 'Gagal memblokir IP' });
    } finally {
      setBlocking(false);
    }
  }

  async function handleSyncDomains() {
    setSyncing(true);
    setSyncMsg(null);
    try {
      const res = await api.post('/cloudflare/sync-domains');
      if (res.data.success) {
        setSyncMsg({ 
          type: 'success', 
          text: `✅ Berhasil import ${res.data.imported} domain/subdomain dari ${res.data.totalZones} zones Cloudflare! (${res.data.skipped} sudah ada sebelumnya)` 
        });
      } else {
        setSyncMsg({ type: 'error', text: res.data.message });
      }
    } catch (err) {
      setSyncMsg({ type: 'error', text: err.response?.data?.message || 'Gagal sync domains' });
    } finally {
      setSyncing(false);
    }
  }

  function handleQuickBlock(ip) {
    setBlockIP(ip);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  const totalPages = Math.ceil(total / limit);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold text-white flex items-center gap-2">
            <CloudIcon className="w-6 h-6 text-cyber-accent" /> Security Logs
          </h1>
          <p className="text-gray-500 text-sm mt-0.5">Log firewall dan security events</p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={handleSyncDomains} disabled={syncing} className="cyber-btn-ghost flex items-center gap-2">
            <CloudIcon className={`w-4 h-4 ${syncing ? 'animate-spin' : ''}`} />
            {syncing ? 'Syncing...' : 'Sync Domains'}
          </button>
          <button onClick={fetchLogs} disabled={loading} className="cyber-btn-ghost flex items-center gap-2">
            <ArrowPathIcon className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            Fetch Logs
          </button>
        </div>
      </div>

      {syncMsg && (
        <div className={`${syncMsg.type === 'success' ? 'bg-green-900 bg-opacity-30 border-green-600' : 'bg-red-900 bg-opacity-30 border-red-600'} border border-opacity-40 rounded-lg px-4 py-3 text-sm`}>
          <div className="flex items-start gap-3">
            <div className={`${syncMsg.type === 'success' ? 'text-green-400' : 'text-red-400'} text-xl`}>
              {syncMsg.type === 'success' ? '✓' : '✗'}
            </div>
            <div className="flex-1">
              <div className={`${syncMsg.type === 'success' ? 'text-green-400' : 'text-red-400'} font-semibold mb-1`}>
                {syncMsg.type === 'success' ? 'Sync Berhasil!' : 'Sync Gagal'}
              </div>
              <div className={`${syncMsg.type === 'success' ? 'text-green-300' : 'text-red-300'} text-xs mb-2`}>{syncMsg.text}</div>
              {syncMsg.type === 'success' && (
                <div className="mt-2">
                  <a href="/domain-monitoring" className="cyber-btn-primary text-xs inline-flex items-center gap-2">
                    📊 Lihat di Domain Monitoring →
                  </a>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      <div className="cyber-card p-5">
        <h2 className="text-sm font-medium text-gray-300 mb-4 flex items-center gap-2">
          <NoSymbolIcon className="w-4 h-4 text-cyber-red" /> Blokir IP Address
        </h2>
        <form onSubmit={handleBlock} className="flex flex-wrap gap-3">
          <input type="text" className="cyber-input flex-1 min-w-48" placeholder="IP Address (contoh: 1.2.3.4)"
            value={blockIP} onChange={(e) => setBlockIP(e.target.value)} required />
          <input type="text" className="cyber-input flex-1 min-w-48" placeholder="Alasan (opsional)"
            value={blockReason} onChange={(e) => setBlockReason(e.target.value)} />
          <button type="submit" disabled={blocking} className="cyber-btn-danger flex items-center gap-2 whitespace-nowrap">
            <NoSymbolIcon className="w-4 h-4" />
            {blocking ? 'Memblokir...' : 'Blokir IP'}
          </button>
        </form>
        {blockMsg && (
          <div className={`mt-3 text-sm px-3 py-2 rounded-lg ${blockMsg.type === 'success' ? 'bg-green-900 bg-opacity-30 text-green-400' : 'bg-red-900 bg-opacity-30 text-red-400'}`}>
            {blockMsg.text}
          </div>
        )}
      </div>

      <div className="cyber-card p-6 border border-cyber-border">
        {/* Sampled logs Header & Description */}
        <div className="mb-6 border-b border-cyber-border pb-4">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-bold text-white flex items-center gap-2">
              Sampled <span className="text-blue-400">logs</span>
            </h2>
            <div className="flex items-center gap-2 text-xs text-gray-400">
              <FunnelIcon className="w-4 h-4 text-cyber-accent" />
              <span>Halaman {page} dari {totalPages || 1}</span>
            </div>
          </div>
          <p className="text-xs text-gray-400 mt-1">
            These logs are based on adaptive sampling of your incoming HTTP/S traffic across all synced domains & subdomains. Use the filters below to narrow down results.
          </p>
        </div>

        {/* Filters bar */}
        <div className="flex flex-wrap items-center gap-3 mb-5">
          <div className="flex-1 min-w-[220px]">
            <label className="block text-xs font-semibold text-gray-400 mb-1">Host / Domain Filter</label>
            <select
              value={hostFilter}
              onChange={(e) => { setHostFilter(e.target.value); setPage(1); }}
              className="cyber-input text-xs w-full bg-cyber-bg text-gray-200"
            >
              <option value="">Semua Host & Subdomain ({availableHosts.length})</option>
              {availableHosts.map((h, i) => (
                <option key={i} value={h}>{h}</option>
              ))}
            </select>
          </div>

          <div className="flex-1 min-w-[220px]">
            <label className="block text-xs font-semibold text-gray-400 mb-1">Cari (IP / Path / Rule)</label>
            <input
              type="text"
              value={search}
              onChange={(e) => { setSearch(e.target.value); setPage(1); }}
              placeholder="Cari IP Address, /path, atau Rule ID..."
              className="cyber-input text-xs w-full"
            />
          </div>

          {(hostFilter || search) && (
            <div className="self-end pb-0.5">
              <button
                onClick={() => { setHostFilter(''); setSearch(''); setPage(1); }}
                className="text-xs text-cyber-red hover:underline"
              >
                Reset Filter
              </button>
            </div>
          )}
        </div>

        {loading ? (
          <div className="text-center py-12 text-gray-500 animate-pulse">Memuat logs...</div>
        ) : logs.length === 0 ? (
          <div className="text-center py-12 text-gray-600">
            <CloudIcon className="w-12 h-12 mx-auto mb-3 opacity-30 text-cyber-accent" />
            <p className="text-sm font-medium text-gray-400">Belum ada security logs terdeteksi.</p>
            <p className="text-xs text-gray-500 mt-1">Klik "Fetch Logs" atau jalankan sync domain untuk menarik data terbaru.</p>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full text-left text-xs border-collapse">
                <thead>
                  <tr className="border-b border-cyber-border text-gray-400 uppercase tracking-wider font-semibold">
                    <th className="py-3 px-3">Time ⓘ</th>
                    <th className="py-3 px-3">Source IP ⓘ</th>
                    <th className="py-3 px-3">Host ⓘ</th>
                    <th className="py-3 px-3">Path ⓘ</th>
                    <th className="py-3 px-3">Action</th>
                    <th className="py-3 px-3">Severity</th>
                    <th className="py-3 px-3">Aksi</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-cyber-border divide-opacity-30">
                  {logs.map((log) => {
                    const hostName = log.host || (log.source ? log.source.replace('cloudflare:', '') : '—');
                    const pathName = log.path || '/';
                    const formattedTime = log.timestamp 
                      ? `${new Date(log.timestamp).toISOString().replace('T', ' ').substring(0, 19)} GMT+7`
                      : '—';

                    return (
                      <tr key={log.id} className="hover:bg-cyber-card/60 transition-colors">
                        <td className="py-3.5 px-3 text-blue-300 font-mono whitespace-nowrap">
                          {formattedTime}
                        </td>
                        <td className="py-3.5 px-3 font-mono text-cyan-400 font-medium whitespace-nowrap">
                          <span className="inline-flex items-center gap-1">
                            {log.ip_address || '—'}
                            <span className="text-gray-500 text-[10px]">↗</span>
                          </span>
                        </td>
                        <td className="py-3.5 px-3 text-gray-200 font-medium whitespace-nowrap max-w-xs truncate" title={hostName}>
                          <span className="inline-flex items-center gap-1 text-cyber-accent">
                            {hostName}
                            <span className="text-gray-500 text-[10px]">↗</span>
                          </span>
                        </td>
                        <td className="py-3.5 px-3 text-gray-300 font-mono whitespace-nowrap max-w-xs truncate" title={pathName}>
                          {pathName}
                        </td>
                        <td className="py-3.5 px-3">
                          <ActionBadge action={log.action} />
                        </td>
                        <td className="py-3.5 px-3">
                          <SeverityBadge severity={log.severity} />
                        </td>
                        <td className="py-3.5 px-3 whitespace-nowrap">
                          {log.ip_address && (
                            <button
                              onClick={() => handleQuickBlock(log.ip_address)}
                              className="text-xs text-red-400 hover:text-red-300 hover:underline inline-flex items-center gap-1 font-medium"
                            >
                              <NoSymbolIcon className="w-3.5 h-3.5" /> Blokir
                            </button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            <div className="flex items-center justify-between mt-5 pt-4 border-t border-cyber-border text-xs">
              <button 
                onClick={() => setPage((p) => Math.max(1, p - 1))} 
                disabled={page === 1} 
                className="cyber-btn-ghost text-xs disabled:opacity-40"
              >
                ← Sebelumnya
              </button>
              <span className="text-gray-400">Halaman {page} dari {totalPages || 1} ({total} total log)</span>
              <button 
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))} 
                disabled={page >= totalPages} 
                className="cyber-btn-ghost text-xs disabled:opacity-40"
              >
                Berikutnya →
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function ActionBadge({ action }) {
  const map = { block: 'bg-red-900 text-red-300', challenge: 'bg-yellow-900 text-yellow-300', js_challenge: 'bg-orange-900 text-orange-300', managed_challenge: 'bg-orange-900 text-orange-300', allow: 'bg-green-900 text-green-300', log: 'bg-gray-800 text-gray-400' };
  return <span className={`px-2 py-0.5 rounded text-xs ${map[action] || 'bg-gray-800 text-gray-400'}`}>{action || '—'}</span>;
}

function SeverityBadge({ severity }) {
  const map = { low: 'bg-green-900 text-green-300', medium: 'bg-yellow-900 text-yellow-300', high: 'bg-orange-900 text-orange-300', critical: 'bg-red-900 text-red-300' };
  return <span className={`px-2 py-0.5 rounded text-xs ${map[severity] || 'bg-gray-800 text-gray-400'}`}>{severity || '—'}</span>;
}
